import { PAGES } from "@dune/types";
import { Button } from "@dune/ui";
import { ArrowLeft } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImportEmailAttachments } from "../../../api/mutations/mail.mutations";
import { useGetBrokerById } from "../../../api/queries/brokers.queries";
import {
	useGetBrokerCommunications,
	useGetBrokerDocuments,
	useGetBrokerTasks,
	useGetBrokerUnprocessedCommunicationsCount,
} from "../../../api/queries/societies.queries";
import { useMe } from "../../../api/queries/users.queries";
import ErrorBoundary from "../../../component/common/ErrorBoundary";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../../component/layout/PageLayoutWithTabs";
import { useEntityPanelSync } from "../../../hooks/useEntityPanelSync";
import { usePagination } from "../../../hooks/usePagination";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { BrokerEmailComposerPanel } from "./components/panels/BrokerEmailComposerPanel";
import BrokerTaskDetailsPanel from "./components/panels/BrokerTaskDetailsPanel";
import AccountingTab from "./components/tabs/AccountingTab";
import AgreementTab from "./components/tabs/AgreementTab";
import BrokerTab from "./components/tabs/BrokerTab";
import CommunicationsTab from "./components/tabs/CommunicationsTab";
import DocumentsTab from "./components/tabs/DocumentsTab";
import TasksTab from "./components/tabs/TasksTab";
import UsersTab from "./components/tabs/UsersTab";
import { BrokerStatusBadge } from "../../../component/brokers/BorkerStatusBadge";
import { generateBrokerEmailWithFallback } from "../../../utils/emails.utils";

const BrokersPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useRequiredParams<{ id: string }>();
	const brokerId = Number.parseInt(id);
	const { data: broker, isLoading, error } = useGetBrokerById(brokerId);
	const { me } = useMe();

	const pagination = usePagination({
		initialLimit: 10,
		initialSortBy: "sentAt",
		initialSortOrder: "desc",
	});

	const {
		data: communicationsResponse,
		isLoading: commLoading,
		error: commError,
	} = useGetBrokerCommunications(brokerId, pagination.params);

	const {
		data: tasks,
		isLoading: tasksLoading,
		error: tasksError,
	} = useGetBrokerTasks(brokerId);

	const { data: documents, error: docsError } = useGetBrokerDocuments(brokerId);

	const { data: unprocessedCommunicationsCount = 0 } =
		useGetBrokerUnprocessedCommunicationsCount(brokerId);

	const handleLimitChange = (newLimit: number) => {
		pagination.setLimit(newLimit);
		pagination.setPage(1);
	};

	const todoTasksCount = useMemo(() => {
		if (!tasks) return 0;
		return tasks.filter((task) => task.status === "TO_DO").length;
	}, [tasks]);

	const toProcessDocumentsCount = useMemo(() => {
		if (!documents) return 0;
		return documents.filter(
			(doc) => doc.file.humanProcessingStatus === "TO_BE_PROCESSED",
		).length;
	}, [documents]);

	const panelTasks = useMemo(() => {
		if (!tasks) return [];
		return tasks.map((task) => ({
			...task,
			status: task.status,
			relatedEntities: task.relatedEntities.map((entity) => ({
				entityType: entity.entityType,
				entityId: entity.entityId,
			})),
			cta: task.cta || undefined,
		}));
	}, [tasks]);

	const tabs: Tab[] = [
		{ value: "apporteur", label: "Apporteur" },
		{ value: "convention", label: "Convention" },
		{ value: "utilisateurs", label: "Utilisateurs" },
		{ value: "comptabilite", label: "Comptabilité" },
		{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
		{
			value: "communications",
			label: "Communications",
			notificationCount: unprocessedCommunicationsCount,
		},
		{
			value: "documents",
			label: "Documents",
			notificationCount: toProcessDocumentsCount,
		},
	];

	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault("apporteur"),
	);

	const { mutate: importEmailAttachments } =
		useImportEmailAttachments(brokerId);

	const panelComponents = useMemo(
		() => ({
			TaskDetails: BrokerTaskDetailsPanel,
			EmailComposer: BrokerEmailComposerPanel,
		}),
		[],
	);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (entityId: number, emailRoutingUuid?: string | null) =>
				generateBrokerEmailWithFallback(emailRoutingUuid, entityId),
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({
					emailAttachmentId,
				});
			},
		}),
		[importEmailAttachments],
	);

	useEntityPanelSync({
		entityType: "BROKER",
		entityId: brokerId,
		communications: communicationsResponse?.data ?? [],
		tasks: panelTasks,
		emailRoutingUuid: broker?.emailRoutingUuid,
		components: panelComponents,
		emailConfig: panelEmailConfig,
	});

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !broker) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations de l'apporteur."
				tabCount={tabs.length}
			/>
		);
	}

	if (commError) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des communications de l'apporteur."
				tabCount={tabs.length}
			/>
		);
	}

	if (tasksError) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des tâches de l'apporteur."
				tabCount={tabs.length}
			/>
		);
	}

	if (docsError) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des documents de l'apporteur."
				tabCount={tabs.length}
			/>
		);
	}

	const tabContent: { [key: string]: React.ReactNode } = {
		apporteur: <BrokerTab broker={broker} />,
		convention: <AgreementTab />,
		utilisateurs: <UsersTab />,
		comptabilite: <AccountingTab />,
		taches: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des tâches.">
				<TasksTab tasks={tasks} isLoading={tasksLoading} error={tasksError} />
			</ErrorBoundary>
		),
		communications: (
			<CommunicationsTab
				brokerId={brokerId}
				emailRoutingUuid={broker.emailRoutingUuid}
				communicationsResponse={communicationsResponse}
				isLoading={commLoading}
				error={commError}
				onPageChange={pagination.handlePageChange}
				onSortChange={pagination.handleSortChange}
				onLimitChange={handleLimitChange}
			/>
		),
		documents: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des documents.">
				<DocumentsTab />
			</ErrorBoundary>
		),
	};

	// Déterminer le titre et les actions selon le rôle de l'utilisateur
	const isBroker = me?.role === "BROKER";
	const pageTitle = isBroker ? (
		"Administration"
	) : (
		<div className="flex items-center gap-2">
			{broker.socialReasons}
			<BrokerStatusBadge status={broker.status} />
		</div>
	);
	const topActions = isBroker ? null : (
		<Button
			variant="ghost"
			onClick={() => navigate(PAGES.BROKER_LIST)}
			className="has-[>svg]:px-1"
		>
			<ArrowLeft />
			Retour à la liste des apporteurs
		</Button>
	);

	return (
		<PageLayoutWithTabs
			title={pageTitle}
			tabs={tabs}
			activeTab={activeTab}
			onTabChange={setActiveTab}
			tabContent={tabContent}
			topActions={topActions}
		/>
	);
};

export default BrokersPage;
